import { ChevronLeft, ChevronRight, Download, ExternalLink, Share2, X } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { isHubIosShell } from "@/lib/hubShell";
import { downloadAttachmentHref, openWithAttachmentHref, shareAttachmentHref } from "@/mail/attachmentOpen";

export type MailAttachPreviewItem = {
  href: string;
  label: string;
  mimeType?: string;
};

type Props = {
  items: MailAttachPreviewItem[];
  index: number;
  onIndexChange: (next: number) => void;
  onClose: () => void;
};

function itemIsImage(item: MailAttachPreviewItem): boolean {
  const mime = (item.mimeType || "").toLowerCase();
  const href = item.href || "";
  const label = item.label || "";
  if (mime.startsWith("image/")) return true;
  if (href.startsWith("data:image/")) return true;
  // blob: object URLs have no mime in the href — trust the filename.
  if (href.startsWith("blob:") && /\.(png|jpe?g|gif|webp|bmp|heic|heif)$/i.test(label)) {
    return true;
  }
  return /\.(png|jpe?g|gif|webp|bmp|heic|heif)$/i.test(label);
}

function itemIsPdf(item: MailAttachPreviewItem): boolean {
  const mime = (item.mimeType || "").toLowerCase();
  const href = item.href || "";
  const label = item.label || "";
  return (
    mime === "application/pdf" ||
    href.startsWith("data:application/pdf") ||
    /\.pdf$/i.test(label)
  );
}

/**
 * In-app viewer for mail attachments (images + PDF). Tip 132 / 142.
 * Avoids window.open(data:) / auth-walled API URLs that fail on phone.
 * Tip 142: working Download + Share on iOS; swipe / next for multi-image.
 */
export function MailAttachmentPreview({ items, index, onIndexChange, onClose }: Props) {
  const safeItems = items.length ? items : [];
  const safeIndex = Math.min(Math.max(0, index), Math.max(0, safeItems.length - 1));
  const current = safeItems[safeIndex];
  const multi = safeItems.length > 1;
  const [busy, setBusy] = useState<"download" | "share" | "open" | "">("");
  const [actionError, setActionError] = useState("");
  const [imgBroken, setImgBroken] = useState(false);
  const touchStart = useRef<{ x: number; y: number } | null>(null);

  useEffect(() => {
    setImgBroken(false);
    setActionError("");
  }, [current?.href, safeIndex]);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  const go = useCallback(
    (delta: number) => {
      if (!multi) return;
      const next = (safeIndex + delta + safeItems.length) % safeItems.length;
      onIndexChange(next);
      setActionError("");
    },
    [multi, onIndexChange, safeIndex, safeItems.length],
  );

  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      if (event.key === "Escape") {
        onClose();
        return;
      }
      if (event.key === "ArrowLeft") go(-1);
      if (event.key === "ArrowRight") go(1);
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [go, onClose]);

  if (!current) return null;

  const isImage = itemIsImage(current);
  // blob: images without extension still preview as <img> when mime says image/*
  const showAsImage =
    !imgBroken && (isImage || (current.mimeType || "").startsWith("image/"));
  const isPdf = itemIsPdf(current);
  // iOS draws PDF/Office files as a fullscreen viewer that covers this toolbar.
  const showFrame = isPdf && !isHubIosShell();

  async function onDownload() {
    if (busy) return;
    setBusy("download");
    setActionError("");
    try {
      await downloadAttachmentHref(current.href, current.label);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : "Could not download.");
    } finally {
      setBusy("");
    }
  }

  async function onOpenWith() {
    if (busy) return;
    setBusy("open");
    setActionError("");
    try {
      await openWithAttachmentHref(current.href, current.label, current.mimeType);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : "Could not open this file.");
    } finally {
      setBusy("");
    }
  }

  async function onShare() {
    if (busy) return;
    setBusy("share");
    setActionError("");
    try {
      await shareAttachmentHref(current.href, current.label);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : "Could not share.");
    } finally {
      setBusy("");
    }
  }

  function onTouchStart(event: React.TouchEvent) {
    if (!multi) return;
    const t = event.changedTouches[0];
    if (!t) return;
    touchStart.current = { x: t.clientX, y: t.clientY };
  }

  function onTouchEnd(event: React.TouchEvent) {
    if (!multi || !touchStart.current) return;
    const t = event.changedTouches[0];
    if (!t) {
      touchStart.current = null;
      return;
    }
    const dx = t.clientX - touchStart.current.x;
    const dy = t.clientY - touchStart.current.y;
    touchStart.current = null;
    if (Math.abs(dx) < 56 || Math.abs(dx) < Math.abs(dy) * 1.25) return;
    go(dx < 0 ? 1 : -1);
  }

  // Tip 209: portal to body so overflow/transform on .mail-layout / .hub-content
  // cannot clip or swallow the viewer (common WKWebView / Mac app failure mode).
  return createPortal(
    <div
      className="mail-attach-preview-scrim"
      role="dialog"
      aria-modal="true"
      aria-label={current.label}
      onClick={onClose}
    >
      <div
        className="mail-attach-preview-sheet"
        onClick={(event) => event.stopPropagation()}
      >
        <header className="mail-attach-preview-toolbar">
          <strong className="mail-attach-preview-title">
            {current.label}
            {multi ? (
              <span className="mail-attach-preview-count">
                {" "}
                ({safeIndex + 1}/{safeItems.length})
              </span>
            ) : null}
          </strong>
          <div className="mail-attach-preview-actions">
            <button
              type="button"
              className="icon-btn"
              aria-label="Download"
              title="Download"
              disabled={Boolean(busy)}
              onClick={() => void onDownload()}
            >
              <Download size={18} />
            </button>
            <button
              type="button"
              className="icon-btn"
              aria-label="Share"
              title="Share"
              disabled={Boolean(busy)}
              onClick={() => void onShare()}
            >
              <Share2 size={18} />
            </button>
            {showAsImage ? null : (
              <button
                type="button"
                className="icon-btn"
                aria-label="Open with"
                title="Open with"
                disabled={Boolean(busy)}
                onClick={() => void onOpenWith()}
              >
                <ExternalLink size={18} />
              </button>
            )}
            <button
              type="button"
              className="icon-btn"
              aria-label="Close"
              title="Close"
              onClick={onClose}
            >
              <X size={18} />
            </button>
          </div>
        </header>
        {actionError ? (
          <p className="mail-attach-preview-error" role="alert">
            {actionError}
          </p>
        ) : null}
        <div
          className="mail-attach-preview-body"
          onTouchStart={onTouchStart}
          onTouchEnd={onTouchEnd}
        >
          {multi ? (
            <button
              type="button"
              className="mail-attach-preview-nav prev"
              aria-label="Previous attachment"
              onClick={() => go(-1)}
            >
              <ChevronLeft size={22} />
            </button>
          ) : null}
          {showAsImage ? (
            <img
              src={current.href}
              alt={current.label}
              className="mail-attach-preview-img"
              draggable={false}
              onError={() => setImgBroken(true)}
            />
          ) : showFrame ? (
            <iframe title={current.label} src={current.href} className="mail-attach-preview-frame" />
          ) : (
            <div className="mail-attach-preview-fallback">
              <p>
                {imgBroken
                  ? "Could not display this image."
                  : "Preview stays in Hub. Use Open with to view this file, save it, or send it to another app."}
              </p>
              <button
                type="button"
                className="primary-btn"
                disabled={Boolean(busy)}
                onClick={() => void onOpenWith()}
              >
                Open with
              </button>
              <button
                type="button"
                className="hub-external-link-btn hub-external-link-btn-secondary"
                disabled={Boolean(busy)}
                onClick={() => void onDownload()}
              >
                Download {current.label}
              </button>
            </div>
          )}
          {multi ? (
            <button
              type="button"
              className="mail-attach-preview-nav next"
              aria-label="Next attachment"
              onClick={() => go(1)}
            >
              <ChevronRight size={22} />
            </button>
          ) : null}
        </div>
      </div>
    </div>,
    document.body,
  );
}
